/**
 * Builder conversations for the current org/user.
 *
 * The default team-conversations list excludes `agent_type=entity_builder`
 * conversations (see `apps/reasoning/viewsets/team_conversations.py`) so they
 * don't bury ordinary chats. This hook fetches the explicit-opt-in filtered
 * slice — the only way chat history can find its way back to a builder
 * conversation once the user has navigated away from it.
 */

import { useQuery } from '@tanstack/react-query';

import { apiGet } from '@/lib/http/client';
import type { ConversationLifecycleState } from '@/lib/reasoning/conversationLifecycleState';

export interface BuilderConversationListItem {
  id: string;
  title: string;
  initial_prompt: string;
  state: ConversationLifecycleState;
  created_at: string;
  /** Entity type being built, e.g. 'ahoy_validation_rule'. Always set. */
  builder_entity_type: string | null;
  /** The entity being built. Null until the builder's first proposal forms. */
  builder_entity_id: string | null;
}

interface BuilderConversationsResponse {
  conversations: BuilderConversationListItem[];
}

export function useBuilderConversations(
  orgId: string | number | undefined,
  options?: { enabled?: boolean }
) {
  const enabled = (options?.enabled ?? true) && !!orgId;

  return useQuery({
    queryKey: ['reasoning', 'conversations', orgId, 'entity_builder'],
    queryFn: () =>
      apiGet<BuilderConversationsResponse>(
        `/api/orgs/${orgId}/reasoning/conversations/?agent_type=entity_builder`
      ),
    enabled,
    staleTime: 30_000,
  });
}
